import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api, { fmtDateTime } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MemoDoc } from "@/pages/hris/PayrollMemo";

const isDate = (v) => /^\d{4}-\d{2}-\d{2}T/.test(v);

const Fields = ({ fields }) => (
  <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="task-detail-fields">
    {fields.map((f) => (
      <div key={f.label} className={f.label === "Reason" || f.label === "Justification" ? "sm:col-span-2" : ""}>
        <dt className="text-[11px] uppercase tracking-wide text-slate-400">{f.label}</dt>
        <dd className="text-sm text-slate-800 whitespace-pre-wrap break-words">{(isDate(f.value) ? fmtDateTime(f.value) : f.value) || "—"}</dd>
      </div>
    ))}
  </dl>
);

const Lines = ({ lines }) => !lines.length ? null : (
  <div className="mt-5 overflow-x-auto rounded-lg border border-slate-200" data-testid="task-detail-lines">
    <table className="w-full text-xs">
      <thead className="bg-slate-50 text-slate-500"><tr><th className="text-left p-2">Item</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Unit Price</th><th className="text-right p-2">Amount</th></tr></thead>
      <tbody>{lines.map((l, i) => (
        <tr key={`${l.description}-${i}`} className="border-t border-slate-100"><td className="p-2">{l.description}</td><td className="p-2 text-right">{l.qty}</td><td className="p-2 text-right">{l.unit_price}</td><td className="p-2 text-right font-medium">{l.amount}</td></tr>
      ))}</tbody>
    </table>
  </div>
);

const History = ({ items }) => !items.length ? null : (
  <div className="mt-5" data-testid="task-detail-history">
    <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">History</div>
    <ol className="space-y-1">{items.map((h, i) => (
      <li key={`${h.at}-${i}`} className="text-xs text-slate-600"><span className="text-slate-400">{fmtDateTime(h.at)}</span> · <b>{h.action}</b>{h.by ? ` · ${h.by}` : ""}{h.note || h.reason ? ` — ${h.note || h.reason}` : ""}</li>
    ))}</ol>
  </div>
);

const MemoBody = ({ id, onChanged }) => {
  const [memo, setMemo] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get(`/hris/payroll-memo/memos/${id}`).then(({ data }) => setMemo(data)).catch((e) => setErr(e.response?.data?.detail || "Could not load the Payroll Memo"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (err) return <p className="text-sm text-red-600" data-testid="task-detail-error">{err}</p>;
  if (!memo) return <Loading />;
  return <MemoDoc memo={memo} reload={() => { load(); onChanged(); }} />;
};

const Loading = () => <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"><Icons.Loader2 className="h-4 w-4 animate-spin" />Loading…</div>;

export const TaskDetailDialog = ({ task, onClose, onAct, onChanged }) => {
  const [d, setD] = useState(null);
  const memo = task?.link_type === "payroll_memo";
  useEffect(() => {
    setD(null);
    if (task && !memo) api.get(`/tasks/${task.id}/detail`).then(({ data }) => setD(data)).catch(() => setD({ fields: [], lines: [], history: [], error: true }));
  }, [task, memo]);
  if (!task) return null;
  const pending = task.status === "Pending";
  const inline = ["leave_request", "purchase_request"].includes(task.link_type);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${memo ? "max-w-5xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto`} data-testid="task-detail-dialog">
        <DialogHeader>
          <DialogTitle data-testid="task-detail-title">{task.title}</DialogTitle>
          <DialogDescription>{task.type} · {task.reference}</DialogDescription>
        </DialogHeader>
        {memo ? <MemoBody id={task.link_id} onChanged={onChanged} /> : !d ? <Loading /> : d.error ? <p className="text-sm text-red-600" data-testid="task-detail-error">Could not load the details.</p> : (
          <div><Fields fields={d.fields} /><Lines lines={d.lines} /><History items={d.history} /></div>
        )}
        {!memo && pending && (
          <DialogFooter className="gap-2">
            {inline ? (<>
              <Button variant="outline" className="text-red-600" onClick={() => onAct(task, "reject")} data-testid="task-detail-reject">Reject</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onAct(task, "approve")} data-testid="task-detail-approve"><Icons.Check className="h-4 w-4 mr-1" />Approve</Button>
            </>) : <Button onClick={() => onAct(task, "open")} data-testid="task-detail-open"><Icons.ExternalLink className="h-4 w-4 mr-1" />Open page</Button>}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
